import React, { useMemo, useState } from 'react';
import { FiChevronUp, FiChevronDown, FiFilter } from 'react-icons/fi';
import PostCard from './PostCard';
import PostStatusBadge from './PostStatusBadge';
import PlatformBadge from './PlatformBadge';
import PostEmptyState from './PostEmptyStates';
import { formatPstShort, formatPstDisplay } from './SchedulePicker';
import { POST_STATUSES } from '../../services/SocialPostService';

const QUEUE_STATUSES = [POST_STATUSES.approved, POST_STATUSES.queued, POST_STATUSES.publishing];

export default function PostQueueList({
  posts = [],
  onEdit,
  onApprove,
  onQueue,
  onCancel,
  onRetry,
  onClick,
  loading = false,
}) {
  const [sortAsc, setSortAsc] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('card');

  const queuePosts = useMemo(() => {
    let filtered = posts.filter((p) => QUEUE_STATUSES.includes(p.status));
    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }
    return filtered.sort((a, b) => {
      const dateA = a.scheduled_at ? new Date(a.scheduled_at) : new Date('9999');
      const dateB = b.scheduled_at ? new Date(b.scheduled_at) : new Date('9999');
      return sortAsc ? dateA - dateB : dateB - dateA;
    });
  }, [posts, sortAsc, statusFilter]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse h-24 bg-zinc-100 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FiFilter className="w-4 h-4 text-zinc-400" />
          {['all', ...QUEUE_STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`
                px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                ${statusFilter === s
                  ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                  : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'
                }
              `}
            >
              {s === 'all' ? 'All Queue' : s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setSortAsc(!sortAsc)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
          >
            {sortAsc ? <FiChevronUp className="w-3.5 h-3.5" /> : <FiChevronDown className="w-3.5 h-3.5" />}
            {sortAsc ? 'Soonest first' : 'Latest first'}
          </button>
          <div className="flex rounded-lg overflow-hidden border border-zinc-200">
            {['card', 'table'].map((m) => (
              <button
                key={m}
                onClick={() => setViewMode(m)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === m ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-zinc-500 hover:bg-zinc-50'
                }`}
              >
                {m === 'card' ? 'Cards' : 'Table'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {queuePosts.length === 0 ? (
        <PostEmptyState type="queue" />
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {queuePosts.map((post) => (
            <PostCard
              key={post._id}
              post={post}
              onEdit={onEdit}
              onApprove={onApprove}
              onQueue={onQueue}
              onCancel={onCancel}
              onRetry={onRetry}
              onClick={onClick}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Post</th>
                <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Platform</th>
                <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Scheduled (PST)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {queuePosts.map((post) => (
                <tr
                  key={post._id}
                  onClick={() => onClick?.(post)}
                  className="hover:bg-zinc-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <p className="text-sm text-zinc-700 line-clamp-1 max-w-xs">
                      {post.content?.slice(0, 80) || <span className="italic text-zinc-400">No content</span>}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <PlatformBadge platform={post.platform} size="xs" />
                  </td>
                  <td className="px-4 py-3">
                    <PostStatusBadge status={post.status} size="xs" />
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
                    {post.scheduled_at ? formatPstShort(post.scheduled_at) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Queue summary */}
      {queuePosts.length > 0 && (
        <div className="text-xs text-zinc-400 text-center pt-2">
          {queuePosts.length} post{queuePosts.length !== 1 ? 's' : ''} in queue
        </div>
      )}
    </div>
  );
}
